import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Table,
  Label,
  Loader,
  Icon,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../../api';

export default function ExternalDocuments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchApi(`${getApiPath()}/@qms/external`)
      .then((json) => {
        setData(json);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <Container style={{ paddingTop: '3em' }}>
        <Loader active inline="centered" content="Loading external documents..." />
      </Container>
    );
  }
  if (error) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Segment color="red">Error: {error}</Segment>
      </Container>
    );
  }

  // Find the most recent updated_at across all items
  const lastUpdated = (data?.items || []).reduce((latest, doc) => {
    const d = doc.updated_at ? new Date(doc.updated_at) : null;
    return d && (!latest || d > latest) ? d : latest;
  }, null);

  // Collect unique owners
  const owners = [...new Set((data?.items || []).map((d) => d.owner).filter(Boolean))];

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '3em' }}>
      <Helmet title="External Standards" />
      <Link to="/qms">&larr; QMS Dashboard</Link>

      <div className="polyglot-page-header">
        <h1>External Standards and Regulations</h1>
        <p className="subtitle">
          {data?.items_total} referenced documents (per SOP-DC-001 &sect;12.1)
        </p>
      </div>

      {/* Audit metadata */}
      <Segment secondary size="small" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.8em 1em' }}>
        <div>
          <Icon name="user" /> <strong>Owner{owners.length > 1 ? 's' : ''}:</strong>{' '}
          {owners.length > 0 ? owners.join(', ') : <span style={{ color: '#999' }}>unassigned</span>}
        </div>
        <div>
          <Icon name="clock outline" /> <strong>Last updated:</strong>{' '}
          {lastUpdated ? lastUpdated.toLocaleDateString() : '—'}
        </div>
      </Segment>

      <Table compact celled selectable size="small">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Identifier</Table.HeaderCell>
            <Table.HeaderCell>Title</Table.HeaderCell>
            <Table.HeaderCell>Edition</Table.HeaderCell>
            <Table.HeaderCell>Publisher</Table.HeaderCell>
            <Table.HeaderCell>Owner</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
            <Table.HeaderCell>Checked</Table.HeaderCell>
            <Table.HeaderCell>Next Review</Table.HeaderCell>
            <Table.HeaderCell>Notes</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {(data?.items || []).map((doc) => (
            <Table.Row key={doc.document_id}>
              <Table.Cell>
                {doc.url ? (
                  <a href={doc.url} target="_blank" rel="noopener noreferrer">
                    <strong>{doc.document_id}</strong>
                    {' '}<Icon name="external" size="small" style={{ opacity: 0.5 }} />
                  </a>
                ) : (
                  <strong>{doc.document_id}</strong>
                )}
              </Table.Cell>
              <Table.Cell>{doc.title}</Table.Cell>
              <Table.Cell>{doc.edition || '—'}</Table.Cell>
              <Table.Cell>{doc.publisher}</Table.Cell>
              <Table.Cell style={{ fontSize: '0.9em', color: '#555' }}>
                {doc.owner || '—'}
              </Table.Cell>
              <Table.Cell>
                <Label
                  size="tiny"
                  color={doc.current_status === 'current' ? 'green' : 'grey'}
                >
                  {doc.current_status}
                </Label>
              </Table.Cell>
              <Table.Cell style={{ fontSize: '0.85em', color: '#555' }}>
                {doc.last_checked || '—'}
              </Table.Cell>
              <Table.Cell style={{ fontSize: '0.85em' }}>
                {doc.next_check_date ? (
                  new Date(doc.next_check_date) < new Date() ? (
                    <Label size="tiny" color="red">{doc.next_check_date}</Label>
                  ) : (
                    <span style={{ color: '#555' }}>{doc.next_check_date}</span>
                  )
                ) : '—'}
              </Table.Cell>
              <Table.Cell style={{ color: '#555', fontSize: '0.9em', maxWidth: '250px' }}>
                {doc.notes || '—'}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Container>
  );
}
